import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import { REGIONS, TOTAL_BY_REGION, REGION_CONTENT, formatUSD } from '@/pages/demo/revenue/components/revenueData';
import {
  RANGES,
  metricsFor,
  metricsForScope,
  regionsForScope,
  conversionRate,
  sponsorCtr,
  DEMOGRAPHICS,
  TOP_STORIES,
  regionName,
} from './adminData';
import type { RangeKey, RegionMetrics } from './adminData';
import StatusBadge from './StatusBadge';

const fmt = (n: number) => n.toLocaleString('en-US');

function Kpi({ label, value, note, icon }: { label: string; value: string; note?: string; icon: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="font-label uppercase tracking-widest text-[10px] md:text-[11px] text-foreground-500">{label}</p>
        <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0"><i className={icon}></i></span>
      </div>
      <p className="mt-2.5 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-none">{value}</p>
      {note && <p className="mt-1.5 text-[11px] text-foreground-500">{note}</p>}
    </div>
  );
}

function BarList({ title, items, unit }: { title: string; items: { label: string; value: number }[]; unit: 'count' | 'usd' }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <p className="font-heading font-bold text-lg text-foreground-950 mb-4">{title}</p>
      <ul className="space-y-3.5">
        {items.map((i) => (
          <li key={i.label}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground-800 truncate">{i.label}</span>
              <span className="font-label font-semibold text-foreground-600 whitespace-nowrap">{unit === 'usd' ? formatUSD(i.value) : fmt(i.value)}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
              <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(i.value / max) * 100}%` }}></div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RegionalAnalytics() {
  const [range, setRange] = useState<RangeKey>('30d');
  const [scope, setScope] = useState<string>('all');

  const scopeRegions = useMemo(() => regionsForScope(scope), [scope]);
  const totals: RegionMetrics = useMemo(() => metricsForScope(scope, range), [scope, range]);

  const regionItems = useMemo(() => {
    if (scopeRegions.length <= 1) {
      return REGIONS.map((r) => ({ label: r.city, value: metricsFor(r.id, range).visitors }));
    }
    return scopeRegions.map((r) => ({ label: r.city, value: metricsFor(r.id, range).visitors }));
  }, [scopeRegions, range]);

  const storyRows = useMemo(() => {
    const ids = new Set(scopeRegions.map((r) => r.id));
    return TOP_STORIES.filter((s) => scope === 'all' || ids.has(s.regionId));
  }, [scope, scopeRegions]);

  const scopeOptions = [
    { id: 'all', label: 'All regions' },
    { id: 'california', label: 'California' },
    { id: 'arizona', label: 'Arizona' },
    ...REGIONS.map((r) => ({ id: r.id, label: r.city })),
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Regional analytics</h2>
        <DemoBadge label="Sample analytics — not live" />
      </div>

      {/* Filters */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500 w-16">Range</span>
          <div className="inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
            {RANGES.map((r) => (
              <button key={r.id} type="button" onClick={() => setRange(r.id)} className={`px-3.5 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[10px] whitespace-nowrap cursor-pointer transition-colors ${range === r.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}>
                {r.short}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500 w-16">Region</span>
          <div className="flex flex-wrap gap-2">
            {scopeOptions.map((o) => {
              const on = scope === o.id;
              return (
                <button key={o.id} type="button" onClick={() => setScope(o.id)} className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line"></i></span>
        Showing <strong className="font-semibold text-foreground-700">{scopeRegions.length}</strong> {scopeRegions.length === 1 ? 'region' : 'regions'} over the last {RANGES.find((r) => r.id === range)?.short}. Consolidated figures sum each region once — multi-region stories are attributed to their primary region and are never double-counted.
      </p>

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Page views" value={fmt(totals.pageViews)} note="Across selected scope" icon="ri-eye-line" />
        <Kpi label="Visitors" value={fmt(totals.visitors)} note={`${fmt(totals.sessions)} sessions`} icon="ri-user-line" />
        <Kpi label="New subscribers" value={fmt(totals.newSubscribers)} note={`${conversionRate(totals)} conversion`} icon="ri-mail-add-line" />
        <Kpi label="Tournament registrations" value={fmt(totals.registrations)} note={`${fmt(totals.tournamentViews)} event views`} icon="ri-trophy-line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarList title="Audience geography (visitors)" items={regionItems} unit="count" />
        <BarList title="Revenue by region (sample)" items={scopeRegions.map((r) => ({ label: r.city, value: TOTAL_BY_REGION[r.id] }))} unit="usd" />
      </div>

      {/* Regional content & activity overview */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3.5 border-b border-background-200 flex flex-wrap items-center justify-between gap-2">
          <p className="font-heading font-bold text-lg text-foreground-950">Regional content &amp; activity</p>
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Region-level targeting</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                <th className="px-4 py-2.5 font-semibold">Region</th>
                <th className="px-3 py-2.5 font-semibold text-right">Stories</th>
                <th className="px-3 py-2.5 font-semibold text-right">Tournaments</th>
                <th className="px-3 py-2.5 font-semibold text-right">Bulletins</th>
                <th className="px-3 py-2.5 font-semibold text-right">Sponsors</th>
                <th className="px-3 py-2.5 font-semibold text-right">Visitors</th>
                <th className="px-4 py-2.5 font-semibold text-right">Sponsor impr.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {scopeRegions.map((r) => {
                const content = REGION_CONTENT[r.id];
                const m = metricsFor(r.id, range);
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-3 text-foreground-950 font-medium whitespace-nowrap">{r.city}<span className="text-foreground-400">, {r.state === 'California' ? 'CA' : 'AZ'}</span></td>
                    <td className="px-3 py-3 text-right text-foreground-700">{content.news.length}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{content.tournaments.length}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{content.bulletins.length}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{content.sponsors.length}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{fmt(m.visitors)}</td>
                    <td className="px-4 py-3 text-right text-foreground-700">{fmt(m.sponsorImpressions)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="px-4 md:px-5 py-3 text-[11px] text-foreground-500 border-t border-background-200">Content volume is shown per market — a multi-region story appears in each market it targets, but audience and revenue figures are consolidated without double-counting.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Sponsor performance */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="font-heading font-bold text-lg text-foreground-950">Sponsor impressions &amp; clicks</p>
          <p className="mt-3 font-heading font-extrabold text-3xl text-foreground-950">{fmt(totals.sponsorImpressions)}</p>
          <p className="text-xs text-foreground-500">impressions</p>
          <div className="mt-3 flex items-center gap-3">
            <span className="font-heading font-extrabold text-xl text-foreground-950">{fmt(totals.sponsorClicks)}</span>
            <span className="text-xs text-foreground-500">clicks</span>
            <StatusBadge tone="review" className="ml-auto">{sponsorCtr(totals)} CTR</StatusBadge>
          </div>
        </div>

        {/* Newsletter conversions */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="font-heading font-bold text-lg text-foreground-950">Newsletter conversions</p>
          <p className="mt-3 font-heading font-extrabold text-3xl text-foreground-950">{conversionRate(totals)}</p>
          <p className="text-xs text-foreground-500">visitor → subscriber</p>
          <p className="mt-3 text-sm text-foreground-700">{fmt(totals.newSubscribers)} new subscribers this period.</p>
        </div>

        {/* Tournament engagement */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="font-heading font-bold text-lg text-foreground-950">Tournament engagement</p>
          <p className="mt-3 font-heading font-extrabold text-3xl text-foreground-950">{fmt(totals.tournamentViews)}</p>
          <p className="text-xs text-foreground-500">tournament page views</p>
          <p className="mt-3 text-sm text-foreground-700">{fmt(totals.registrations)} sample registrations handled.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Demographics */}
        <div className="lg:col-span-5 bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-heading font-bold text-lg text-foreground-950">Audience demographics</p>
            <StatusBadge tone="neutral">Aggregate examples</StatusBadge>
          </div>
          <ul className="mt-4 space-y-3.5">
            {DEMOGRAPHICS.map((d) => (
              <li key={d.band}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-800">{d.band}</span>
                  <span className="font-label font-semibold text-foreground-600">{d.pct}%</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full bg-accent-500 rounded-full" style={{ width: `${d.pct}%` }}></div>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-foreground-500">Illustrative age bands for the walkthrough — no demographic tracking is enabled.</p>
        </div>

        {/* Top stories */}
        <div className="lg:col-span-7 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="px-4 md:px-5 py-3.5 border-b border-background-200">
            <p className="font-heading font-bold text-lg text-foreground-950">Top-performing stories</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                  <th className="px-4 py-2.5 font-semibold">Story</th>
                  <th className="px-3 py-2.5 font-semibold">Category</th>
                  <th className="px-3 py-2.5 font-semibold">Region</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Views</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {storyRows.map((s) => (
                  <tr key={s.title}>
                    <td className="px-4 py-3 text-foreground-950 font-medium max-w-[260px] truncate">{s.title}</td>
                    <td className="px-3 py-3 text-foreground-600 whitespace-nowrap">{s.category}</td>
                    <td className="px-3 py-3 text-foreground-600 whitespace-nowrap">{regionName(s.regionId)}</td>
                    <td className="px-4 py-3 text-right text-foreground-950 font-semibold">{fmt(s.views)}</td>
                  </tr>
                ))}
                {storyRows.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-sm text-foreground-500">No stories for this scope.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flask-line"></i></span>
        All figures are one internally consistent sample dataset — no live analytics tracking is connected.
      </p>
    </div>
  );
}